// Single source of truth for app-internal URLs that show up in more than
// one render path. Inline-templated paths in a one-off component are fine;
// pull a builder up here once two callers diverge or the path grows query
// params (recommendation tracking, deep links).

import { isUuid } from '@leanin/shared';

export const memberPath = (userId: string): string =>
  `/members/${encodeURIComponent(userId)}`;

// Admin pages link to the admin's projection of the member (engagement,
// reports, audit trail) rather than the public profile. Distinct from
// `memberPath` so admin surfaces never accidentally expose the member-only
// view, and member-only surfaces never escalate into admin tools.
export const adminMemberPath = (userId: string): string =>
  `/admin/members/${encodeURIComponent(userId)}`;

// Group detail pages accept a uuid or a vanity slug; a uuid always resolves
// (the route redirects to the slug), so callers holding only an id can link
// safely.
export const circlePath = (circleId: string): string =>
  `/circles/${encodeURIComponent(circleId)}`;

export const networkPath = (networkId: string): string =>
  `/networks/${encodeURIComponent(networkId)}`;

// Deep link to one comment on a post. The post detail route validates the
// `comment` search param, auto-opens the thread, and scrolls the comment
// into view (expanding its reply thread when the target is a reply).
export const commentPath = (postId: string, commentId: string): string =>
  `/posts/${encodeURIComponent(postId)}?comment=${encodeURIComponent(commentId)}`;

// Admin broadcast composer prefilled with a hand-picked `members` audience
// (`/admin/broadcasts/new?members=<uuid>,<uuid>`), opened by the
// /admin/members bulk "Send email" action. Capped at one member-picker page
// (the server's `ids` limit) so the composer hydrates every chip in a single
// request and the URL stays a few KB.
export const ADMIN_BROADCAST_MEMBERS_MAX = 100;

// The validated id list, or null when the param is absent, malformed, or
// over the cap — the route then renders a blank composer.
export function parseAdminBroadcastMembersParam(
  raw: unknown,
): readonly string[] | null {
  if (typeof raw !== 'string' || raw.length === 0) return null;
  const ids = [...new Set(raw.split(','))];
  if (ids.length > ADMIN_BROADCAST_MEMBERS_MAX) return null;
  return ids.every(isUuid) ? ids : null;
}
